import { useState, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent } from 'framer-motion';
import { BuildingOffice2Icon, GlobeAltIcon, UserGroupIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import PartnerForm from '../components/PartnerForm';
import { getActivePartners } from '../lib/supabase';
import { useSponsorshipTiers } from '../hooks/useSponsorshipTiers';

const CATEGORY_TIERS = [
  'Official Airline',
  'Official Bank',
  'Official Beverage',
  'Official Telecom Partner',
  'Official Automotive Brand',
  'Official Insurance Partner',
  'Official Real Estate Partner',
  'Official Media Partner',
  'Official Logistics Partner',
  'Official Construction Partner',
  'Official Apparel Brand',
  'Official Energy Partner',
] as const;

type CategoryTier = typeof CATEGORY_TIERS[number];

interface CategorySponsor {
  tier: CategoryTier;
  name: string;
  logo: string;
  website?: string;
  description?: string;
}

interface Sponsor {
  id?: string;
  name: string;
  logo: string;
  description?: string;
  website?: string;
  stats?: string[];
  image?: string;
  tier?: string;
}

interface SponsorCategory {
  title: string;
  description: string;
  sponsors: Sponsor[];
}

interface OtherPartner {
  name: string;
  logo: string;
  website?: string;
}

interface Benefit {
  icon: any;
  title: string;
  description: string;
  features: string[];
}


const defaultSponsorCategories: SponsorCategory[] = [
  {
    title: "Tournament Host",
    description: "Experience world-class golf at one of Africa's finest courses",
    sponsors: [
      {
        name: "Ibom Golf & Country Club",
        logo: "https://placehold.co/300x150/png?text=Ibom+Golf+Club",
        description: "The premier golf destination in West Africa, featuring a world-class 18-hole championship course designed by Thomson Perrett & Lobb.",
        website: "https://ibomgolfresort.com",
        stats: ["Established 2015", "18-Hole Championship Course", "5-Star Facilities"],
        image: "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa"
      }
    ]
  },
  {
    title: "Title Sponsors",
    description: "Our primary partners in making the tournament a success",
    sponsors: []
  },
  {
    title: "Premier Partners",
    description: "Key supporters of the tournament's vision",
    sponsors: []
  }
];

const defaultOtherPartners: OtherPartner[] = [
  {
    name: "Le Meridien Hotels",
    logo: "https://placehold.co/200x100/png?text=Le+Meridien",
    website: "https://www.marriott.com/en-us/hotels/quwlm-le-meridien-ibom-hotel-and-golf-resort/overview/"
  },
  {
    name: "First Bank",
    logo: "https://placehold.co/200x100/png?text=First+Bank",
    website: "https://www.firstbanknigeria.com/"
  },
  {
    name: "MTN Nigeria",
    logo: "https://placehold.co/200x100/png?text=MTN",
    website: "https://www.mtn.ng/"
  },
  {
    name: "Zenith Bank",
    logo: "https://placehold.co/200x100/png?text=Zenith",
    website: "https://www.zenithbank.com/"
  },
  {
    name: "Nigerian Breweries",
    logo: "https://placehold.co/200x100/png?text=NB+Plc",
    website: "https://nbplc.com/"
  },
  {
    name: "Total Energies",
    logo: "https://placehold.co/200x100/png?text=Total",
    website: "https://totalenergies.com/"
  },
  {
    name: "DStv",
    logo: "https://placehold.co/200x100/png?text=DStv",
    website: "https://www.dstv.com/en-ng/"
  },
  {
    name: "PwC Nigeria",
    logo: "https://placehold.co/200x100/png?text=PwC",
    website: "https://www.pwc.com/ng/en.html"
  }
];

const benefits: Benefit[] = [
  {
    icon: GlobeAltIcon,
    title: "Global Exposure",
    description: "Reach millions through international marketing and digital exposure",
    features: ["Digital Broadcast", "Social Media", "Press Coverage"]
  },
  {
    icon: UserGroupIcon,
    title: "VIP Access",
    description: "Exclusive networking opportunities with industry leaders",
    features: ["Clubhouse Lounge", "Pro-Am Entry", "Gala Dinner"]
  },
  {
    icon: BuildingOffice2Icon,
    title: "Brand Integration",
    description: "Custom activation opportunities throughout the tournament",
    features: ["On-site Presence", "Digital Integration", "Custom Events"]
  }
];


function Sponsors() {
  const { tiers: dbTiers } = useSponsorshipTiers();
  const displayTiers = dbTiers.filter(t => !t.is_category_tier && t.name !== 'Tournament Host');
  const categoryTierNames = dbTiers
    .filter(t => t.is_category_tier)
    .map(t => t.name);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [sponsorCategories, setSponsorCategories] = useState<SponsorCategory[]>(defaultSponsorCategories);
  const [otherPartners, setOtherPartners] = useState<OtherPartner[]>([]);
  const [categorySponsors, setCategorySponsors] = useState<CategorySponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const { scrollY } = useScroll();

  useEffect(() => {
    fetchPartners();
  }, [dbTiers]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const { data, error } = await getActivePartners();

      if (error) throw error;

      const allCategoryTierNames = categoryTierNames.length > 0
        ? categoryTierNames
        : (CATEGORY_TIERS as readonly string[]);

      if (data && data.length > 0) {
        const hostPartners = data.filter(p => p.tier === 'Tournament Host');
        const diamondPartners = data.filter(p => p.tier === 'Diamond' || p.tier === 'Title Sponsor');
        const platinumPartners = data.filter(p => p.tier === 'Platinum');
        const goldPartners = data.filter(p => p.tier === 'Gold');
        const silverPartners = data.filter(p => p.tier === 'Silver');
        const bronzePartners = data.filter(p => p.tier === 'Bronze');

        const updatedCategories = [...sponsorCategories];

        if (hostPartners.length > 0 && updatedCategories.length > 0 && updatedCategories[0]) {
          updatedCategories[0].sponsors = hostPartners.map(p => ({
            name: p.company_name,
            logo: p.logo_url || `https://placehold.co/300x150/png?text=${encodeURIComponent(p.company_name)}`,
            description: p.description || 'Official host of the Ibom Open Golf Tournament.',
            website: p.website || '#',
            stats: p.stats || ['Tournament Host', 'Official Venue'],
            image: p.image_url || 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa'
          }));
        }

        if (diamondPartners.length > 0 && updatedCategories.length > 1 && updatedCategories[1]) {
          updatedCategories[1].sponsors = diamondPartners.map(p => ({
            name: p.company_name,
            logo: p.logo_url || `https://placehold.co/300x150/png?text=${encodeURIComponent(p.company_name)}`,
            description: p.description || 'Diamond tier sponsor supporting the Ibom Open Golf Tournament.',
            website: p.website || '#',
            stats: p.stats || ['Diamond Sponsor', `₦${(p.contribution / 1000000).toFixed(1)}M Contribution`],
            image: p.image_url || ''
          }));
        }

        if (platinumPartners.length > 0 && updatedCategories.length > 2 && updatedCategories[2]) {
          updatedCategories[2].sponsors = platinumPartners.map(p => ({
            name: p.company_name,
            logo: p.logo_url || `https://placehold.co/300x150/png?text=${encodeURIComponent(p.company_name)}`,
            description: p.description || 'Platinum tier sponsor supporting the Ibom Open Golf Tournament.',
            website: p.website || '#',
            stats: p.stats || ['Platinum Sponsor', `₦${(p.contribution / 1000000).toFixed(1)}M Contribution`],
            image: p.image_url || ''
          }));
        }

        const categorySponsorData: CategorySponsor[] = data
          .filter(p => allCategoryTierNames.includes(p.tier))
          .map(p => ({
            tier: p.tier as CategoryTier,
            name: p.company_name,
            logo: p.logo_url || `https://placehold.co/200x100/png?text=${encodeURIComponent(p.company_name)}`,
            website: p.website || '#',
            description: p.description || undefined
          }));

        setCategorySponsors(categorySponsorData);

        const otherPartnersData = [...goldPartners, ...silverPartners, ...bronzePartners].map(p => ({
          name: p.company_name,
          logo: p.logo_url || `https://placehold.co/200x100/png?text=${encodeURIComponent(p.company_name)}`,
          website: p.website || '#'
        }));

        if (otherPartnersData.length > 0) {
          setOtherPartners(otherPartnersData);
        }

        setSponsorCategories(updatedCategories);
      } else {
        setCategorySponsors([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  useMotionValueEvent(scrollY, "change", (latest) => {
    setShowFloatingButton(latest > window.innerHeight);
  });

  const openPartnerForm = () => {
    setIsFormOpen(true);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-golf-gray text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block bg-golf-orange px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Partnership Opportunities 2025
            </span>
            <h1 className="text-6xl font-display font-bold mb-6">
              Partner With Excellence
            </h1>
            <p className="text-xl text-golf-sand mb-12">
              Join an exclusive community of brands shaping the future of golf in Africa
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="modern-button px-8 py-4 text-lg font-medium"
              onClick={openPartnerForm}
            >
              Become a Partner
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Current Sponsors */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          {sponsorCategories.filter(category => category.sponsors.length > 0).map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-32 last:mb-0"
            >
              <div className="text-center mb-16">
                <h2 className="text-4xl font-display font-bold text-golf-gray mb-4">
                  {category.title}
                </h2>
                <p className="text-xl text-gray-600">{category.description}</p>
              </div>

              {(category.title === "Tournament Host" || category.title === "Title Sponsors") ? (
                <div className="max-w-7xl mx-auto">
                  {category.sponsors.map((sponsor, sponsorIndex) => (
                    <motion.div
                      key={sponsorIndex}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
                    >
                      <div className="grid lg:grid-cols-2">
                        <div className="p-8 lg:p-12">
                          <div className="h-[150px] flex items-center mb-8">
                            <img
                              src={sponsor.logo}
                              alt={sponsor.name}
                              className="max-h-[150px] max-w-[300px] object-contain"
                            />
                          </div>
                          <h3 className="text-3xl font-bold text-golf-gray mb-4">
                            {sponsor.name}
                          </h3>
                          <p className="text-gray-600 text-lg mb-8">
                            {sponsor.description}
                          </p>
                          <div className="grid grid-cols-3 gap-4 mb-8">
                            {sponsor.stats?.map((stat, statIndex) => (
                              <div
                                key={statIndex}
                                className="text-center p-4 bg-golf-sand rounded-lg"
                              >
                                <p className="text-sm font-medium text-golf-gray">
                                  {stat}
                                </p>
                              </div>
                            ))}
                          </div>
                          {sponsor.website && sponsor.website !== '#' && (
                            <a
                              href={sponsor.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-golf-orange hover:text-golf-green transition-colors font-medium"
                            >
                              Visit Website
                              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </a>
                          )}
                        </div>
                        <div className="relative h-full min-h-[400px] bg-golf-gray">
                          {sponsor.image ? (
                            <img
                              src={sponsor.image}
                              alt={sponsor.name}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-200">
                              <p className="text-gray-500">No image available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {category.sponsors.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No {category.title.toLowerCase()} available yet.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="max-w-7xl mx-auto">
                  {category.sponsors.length > 0 ? (
                    <>
                      <Swiper
                        modules={[Autoplay, Navigation]}
                        spaceBetween={30}
                        slidesPerView={1}
                        breakpoints={{
                          640: { slidesPerView: 2 },
                          1024: { slidesPerView: 3 }
                        }}
                        navigation={true}
                        autoplay={{
                          delay: 3000,
                          disableOnInteraction: false,
                          pauseOnMouseEnter: true
                        }}
                        loop={true}
                        className="sponsors-swiper"
                        onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
                      >
                        {category.sponsors.map((sponsor, sponsorIndex) => (
                          <SwiperSlide key={sponsorIndex}>
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 h-full"
                            >
                              <div className="relative h-48">
                                {sponsor.image ? (
                                  <img
                                    src={sponsor.image}
                                    alt={sponsor.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                    <p className="text-gray-500">No image available</p>
                                  </div>
                                )}
                              </div>
                              <div className="p-6">
                                <div className="h-[150px] flex items-center justify-center mb-4">
                                  <img
                                    src={sponsor.logo}
                                    alt={sponsor.name}
                                    className="max-h-[150px] max-w-[300px] object-contain"
                                  />
                                </div>
                                <h3 className="text-xl font-bold text-golf-gray mb-2">
                                  {sponsor.name}
                                </h3>
                                <p className="text-gray-600 text-sm mb-4">
                                  {sponsor.description}
                                </p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {sponsor.stats?.map((stat, statIndex) => (
                                    <span
                                      key={statIndex}
                                      className="text-xs font-medium bg-golf-sand rounded-full px-3 py-1 text-golf-gray"
                                    >
                                      {stat}
                                    </span>
                                  ))}
                                </div>
                                {sponsor.website && sponsor.website !== '#' && (
                                  <a
                                    href={sponsor.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-golf-orange hover:text-golf-green transition-colors font-medium text-sm"
                                  >
                                    Visit Website →
                                  </a>
                                )}
                              </div>
                            </motion.div>
                          </SwiperSlide>
                        ))}
                      </Swiper>

                      {category.sponsors.length > 1 && (
                        <div className="flex justify-center mt-6">
                          <div className="flex items-center space-x-2">
                            {category.sponsors.map((_, index) => (
                              <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                                  activeSlide === index ? 'bg-golf-orange' : 'bg-golf-orange/30'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No {category.title.toLowerCase()} available yet.</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Category Partners Section */}
      {categorySponsors.length > 0 && (
        <section className="py-24 bg-golf-gray text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-7xl mx-auto"
            >
              <div className="text-center mb-16">
                <span className="inline-block bg-golf-orange/20 text-golf-orange px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                  Official Category Partners
                </span>
                <h2 className="text-4xl font-display font-bold text-white mb-4">
                  Category Partners
                </h2>
                <p className="text-lg text-gray-300">
                  Industry leaders holding exclusive official partner status in their respective categories
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categorySponsors.map((sponsor, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    whileHover={{ y: -4 }}
                    className="group"
                  >
                    <div className="bg-white/5 group-hover:bg-white/10 border border-white/10 group-hover:border-golf-orange/40 rounded-2xl overflow-hidden transition-all duration-300 h-full flex flex-col">
                      <div className="px-5 pt-5 pb-2 border-b border-white/10">
                        <span className="text-[10px] font-bold text-golf-orange uppercase tracking-widest">
                          {sponsor.tier}
                        </span>
                      </div>
                      <div className="px-6 py-6 flex flex-col items-center flex-1">
                        <div className="h-16 w-full flex items-center justify-center mb-4 bg-white/10 rounded-xl px-4">
                          <img
                            src={sponsor.logo}
                            alt={sponsor.name}
                            className="max-h-12 max-w-full object-contain brightness-0 invert"
                          />
                        </div>
                        <p className="text-sm font-semibold text-white text-center leading-snug mb-3">
                          {sponsor.name}
                        </p>
                        {sponsor.description && (
                          <p className="text-xs text-gray-400 text-center mb-3 line-clamp-2">
                            {sponsor.description}
                          </p>
                        )}
                        {sponsor.website && sponsor.website !== '#' && (
                          <a
                            href={sponsor.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-auto text-xs text-golf-orange/70 hover:text-golf-orange transition-colors font-medium"
                          >
                            Visit Website →
                          </a>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Other Partners Section */}
      {otherPartners.length > 0 && <section className="py-24 bg-golf-sand">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-7xl mx-auto"
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-display font-bold text-golf-gray mb-4">
                Other Partners
              </h2>
              <p className="text-xl text-gray-600">
                Supporting excellence in African golf
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {otherPartners.map((partner, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                  className="relative group"
                >
                  <motion.div
                    initial={false}
                    whileHover={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 bg-white rounded-xl shadow-md"
                  />
                  <motion.div
                    initial={false}
                    whileHover={{ scale: 1.1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="relative bg-white rounded-xl p-6 flex flex-col items-center justify-center group-hover:shadow-xl transform transition-all duration-300"
                  >
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="w-full h-16 object-contain transition-all duration-300 group-hover:opacity-90 mb-3"
                    />
                    <h3 className="text-sm font-medium text-golf-gray mb-2">{partner.name}</h3>
                    {partner.website && partner.website !== '#' && (
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-golf-orange hover:text-golf-green transition-colors"
                      >
                        Visit Website
                      </a>
                    )}
                    <div className="absolute inset-0 bg-golf-orange/0 group-hover:bg-golf-orange/5 rounded-xl transition-all duration-300" />
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>}


      {/* Partnership Benefits Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-display font-bold text-golf-gray mb-4">
                Partnership Benefits
              </h2>
              <p className="text-xl text-gray-600">
                Unlock exclusive opportunities and maximize your brand's exposure
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg"
                >
                  <benefit.icon className="w-12 h-12 text-golf-orange mb-6" />
                  <h3 className="text-xl font-bold text-golf-gray mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {benefit.description}
                  </p>
                  <ul className="space-y-3">
                    {benefit.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 text-golf-orange mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partnership Tiers Section */}
      <section className="py-24 bg-golf-sand">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-display font-bold text-golf-gray mb-4">
                Partnership Tiers
              </h2>
              <p className="text-xl text-gray-600">
                Choose the perfect partnership level for your brand
              </p>
            </div>

            {/* Scrollable tiers container */}
            <div className="overflow-x-auto pb-6">
              <div className="flex space-x-6 min-w-max">
                {displayTiers.map((tier, index) => (
                  <motion.div
                    key={tier.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 w-80 flex-shrink-0"
                  >
                    <SparklesIcon className="w-12 h-12 text-golf-orange mb-6" />
                    <h3 className="text-2xl font-bold text-golf-gray mb-2">
                      {tier.name}
                    </h3>
                    <p className="text-3xl font-display font-bold text-golf-orange mb-6">
                      {tier.price_label}
                    </p>
                    <ul className="space-y-4 mb-8">
                      {tier.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <svg className="w-5 h-5 text-golf-orange mr-2 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      className="w-full modern-button py-3 text-white font-medium rounded-lg"
                      onClick={openPartnerForm}
                    >
                      Contact Us
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Scroll indicator */}
            <div className="flex justify-center mt-8">
              <div className="flex items-center space-x-2">
                {displayTiers.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-golf-orange' : 'bg-golf-orange/50'}`}
                  />
                ))}
              </div>
            </div>

            {/* Mobile instructions */}
            <div className="text-center mt-4 text-sm text-gray-500 md:hidden">
              Swipe left to see more tiers
            </div>
          </motion.div>
        </div>
      </section>

      {/* Floating Partner Button */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ 
          opacity: showFloatingButton ? 1 : 0,
          y: showFloatingButton ? 0 : 100
        }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-8 right-8 md:right-24 z-50"
      >
        <button
          onClick={openPartnerForm}
          className="modern-button px-6 py-3 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
        >
          Become a Partner
        </button>
      </motion.div>

      {/* Partner Form Modal */}
      <PartnerForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
      />
    </div>
  );
}

export default Sponsors;